import { Outlet } from "react-router-dom";

import { Dropdown, ExportTableDialog, TableSettingsDialog } from "@/ds";
import { ObrasCardsView } from "./components/obras-cards-view";
import { ObrasListaView } from "./components/obras-lista-view";
import { ObrasToolbar } from "./components/obras-toolbar";
import {
  OBRAS_COLUMN_LABELS,
  OBRAS_FUTURE_FEATURE_TITLE,
  OBRAS_MAIN_COLUMNS,
  OBRAS_TAB_DETALHAMENTO,
  OBRAS_TAB_INDICADORES,
  OBRAS_TITLE,
} from "./obras-lista.constants";
import type { ObrasColumnKey, ObrasScreenViewModel } from "./types-obras-lista";
import styles from "./obras-screen.module.scss";

/**
 * Painel de Obras › Detalhamento (HU 2521) — consulta somente leitura das obras em Lista
 * (DataTable) ou Cards (por status), com busca, filtros, filtros salvos e Configurações da
 * Tabela. Lista e Cards compartilham busca, filtros e ordenação (RN02).
 */
export function ObrasScreenView({
  isMobile,
  tabOptions,
  view,
  escolherVisao,
  search,
  buscar,
  filters,
  sort,
  ordenarPor,
  range,
  handleRange,
  settings,
  registrarResultadoDaLista,
  isStatusDisabled,
  statusState,
  statusError,
  statusOptions,
  filterColumns,
  panelValue,
  panelSavedFilters,
  handlePanelChange,
  handleSaveFilter,
  handleApplySavedFilter,
  deleteSavedFilter,
  exportView,
  handleApplySettings,
  clearSearch,
  clearFilters,
  clearStatus,
  changeStatus,
  versaoDaLista,
  contextoDaNovaObra,
}: ObrasScreenViewModel) {
  const containerClassName = `${styles.container} ${view === "cards" ? styles.containerCards : styles.containerLista}`;
  const tabSelectClassName =
    `${styles.tabSelect} ${view === "cards" ? styles.tabSelectCards : ""}`.trim();

  return (
    <section className={containerClassName} data-full-bleed data-testid="obras-screen">
      <header className={styles.header}>
        <h1 className={styles.title}>{OBRAS_TITLE}</h1>

        {isMobile ? (
          <Dropdown
            className={tabSelectClassName}
            value="detalhamento"
            options={tabOptions}
            aria-label="Painel de Obras"
            data-testid="obras-tab-select"
          />
        ) : (
          <nav className={styles.tabs} aria-label="Painel de Obras">
            <button
              type="button"
              className={styles.tab}
              disabled
              title={OBRAS_FUTURE_FEATURE_TITLE}
              data-testid="obras-tab-indicadores"
            >
              {OBRAS_TAB_INDICADORES}
            </button>
            <button
              type="button"
              className={`${styles.tab} ${styles.tabActive}`}
              aria-current="page"
              data-testid="obras-tab-detalhamento"
            >
              {OBRAS_TAB_DETALHAMENTO}
            </button>
          </nav>
        )}
      </header>

      <ObrasToolbar
        isMobile={isMobile}
        view={view}
        onViewChange={escolherVisao}
        search={search}
        onSearchChange={buscar}
        statusValue={filters.statusObra}
        statusOptions={statusOptions}
        isStatusDisabled={isStatusDisabled}
        onStatusChange={changeStatus}
        onStatusClear={clearStatus}
        range={range}
        onRangeChange={handleRange}
        filterDefinitions={filterColumns}
        filterValue={panelValue}
        onFilterChange={handlePanelChange}
        savedFilters={panelSavedFilters}
        onSaveFilter={handleSaveFilter}
        onDeleteSavedFilter={deleteSavedFilter}
        onApplySavedFilter={handleApplySavedFilter}
        onExport={exportView.open}
        onOpenSettings={settings.dialog.open}
      />

      {exportView.isOpen ? (
        <ExportTableDialog
          listName="obras"
          variant="figma"
          isExporting={exportView.isExporting}
          onExport={exportView.run}
          onCancel={exportView.close}
        />
      ) : null}

      <TableSettingsDialog<ObrasColumnKey>
        dialog={settings.dialog}
        columnLabels={OBRAS_COLUMN_LABELS}
        mainColumns={OBRAS_MAIN_COLUMNS}
        showColumnsSection={settings.showColumnsSection}
        testId="obras-table-settings"
        onApplied={handleApplySettings}
      />

      <div className={styles.content}>
        {view === "lista" ? (
          <ObrasListaView
            key={versaoDaLista}
            search={search}
            filters={filters}
            sort={sort}
            onSort={ordenarPor}
            settings={settings}
            isMobile={isMobile}
            onResult={registrarResultadoDaLista}
            onClearSearch={clearSearch}
            onClearFilters={clearFilters}
          />
        ) : (
          <ObrasCardsView
            key={versaoDaLista}
            search={search}
            filters={filters}
            isMobile={isMobile}
            statusOptions={statusOptions}
            statusState={statusState}
            statusError={statusError}
            onClearSearch={clearSearch}
            onClearFilters={clearFilters}
          />
        )}
      </div>

      <Outlet context={contextoDaNovaObra} />
    </section>
  );
}
